import debounce from "lodash/debounce"
import { saveSearchTermBypassLength, MINIMUM_SEARCH_TERM_LENGTH } from "app/components/universal_search/search_history"
import { trackSearchResultClicked, trackExploreResultClicked } from "app/utils/mixpanel_tracking"

// Discover tiles carry plural data-type values ("applets" | "services" | "stories");
// map them to the singular form so `Explore Result Clicked` reports the same
// `result_type` vocabulary as the sibling `Search Result Clicked` event.
const DISCOVER_RESULT_TYPE: Record<string, string> = { applets: "applet", services: "service", stories: "story" }
export const normalizeDiscoverResultType = (type: string): string => DISCOVER_RESULT_TYPE[type] ?? type

// The services panel picks a category with chips, and the chip that carries
// `.active` holds the current selection (see services_filter.ts). Read it per
// click: the panel is fetched on demand, so this markup is replaced after
// TrackEvents() runs. "All" is the unfiltered default, and also the fallback
// when no chip is active, so a panel without chips reports `get_more_service`.
const selectedServiceCategory = (): string => {
  const activeChip = document.querySelector<HTMLElement>(".service-category-chips .service-category-chip.active")

  return activeChip?.dataset.value ?? "All"
}

export const TrackEvents = () => {
  const { searchTerm = "" } = (document.querySelector(".explore-discover-container") as HTMLElement).dataset
  const allContainer = document.querySelector(".explore-discover-results")
  const serviceContainer = document.querySelector("div.web-services-container")
  const searchResultContainer = document.querySelector(".search-view-container")

  const onClickDiscoverItem = (actionName: string, data) => {
    window.App.Utils.logCustomDatadogAction("explore_click_any_result", data)
    window.App.Utils.logCustomDatadogAction(actionName, data)
  }

  const saveShortSearchTermHandler = () => {
    if (searchTerm && searchTerm.length < MINIMUM_SEARCH_TERM_LENGTH) {
      saveSearchTermBypassLength({ type: "search", term: searchTerm })
    }
  }

  allContainer.addEventListener("click", event => {
    const tile = (event.target as Element)?.closest("[data-type]") as HTMLElement | null
    if (!tile) return

    onClickDiscoverItem(`explore_click_get_more_${tile.dataset.type}`, {
      searchResultType: tile.dataset.type,
      searchResultId: tile.dataset.id,
    })

    // Rank = 1-based position among the tiles currently visible in the feed
    // (tab filtering hides tiles via `.hide`), computed from the DOM so it
    // stays correct across async pagination without server-side offset math.
    const visibleTiles = Array.from(allContainer.querySelectorAll<HTMLElement>("[data-type]:not(.hide)"))
    const rank = visibleTiles.indexOf(tile) + 1
    const resultId = tile.dataset.id ?? ""
    // Skip tiles without an id (e.g. the "Create your own Applet" CTA) so they
    // don't pollute the funnel that joins on result_id.
    if (rank > 0 && resultId) {
      trackExploreResultClicked({
        resultType: normalizeDiscoverResultType(tile.dataset.type ?? ""),
        resultId,
        rank,
        isSignedIn: Boolean((window as unknown as { App?: { user?: { id?: unknown } } }).App?.user?.id),
      })
    }

    saveShortSearchTermHandler()
  })

  serviceContainer.addEventListener("click", event => {
    const tile = (event.target as Element)?.closest("[data-type]") as HTMLElement
    if (!tile) return

    const isFiltered = selectedServiceCategory() !== "All"

    onClickDiscoverItem(`explore_click_${isFiltered ? "filtered_category_service_result" : "get_more_service"}`, {
      searchResultType: tile.dataset.type,
      searchResultId: tile.dataset.id,
    })

    saveShortSearchTermHandler()
  })

  searchResultContainer.addEventListener("click", event => {
    const tile = event.target.closest("[data-type]")
    if (!tile) return

    const resultType = tile.dataset.type
    const resultId = tile.dataset.id

    onClickDiscoverItem(`explore_click_${resultType}_search_result`, {
      searchResultType: resultType,
      searchResultId: resultId,
    })

    const rank = Number(tile.dataset.rank)
    if ((resultType === "applet" || resultType === "service") && !isNaN(rank)) {
      trackSearchResultClicked({
        term: searchTerm,
        resultType,
        resultId,
        rank,
      })
    }

    saveShortSearchTermHandler()
  })

  let scrollTimeout: number | null = null

  const debouncedHandleScrollToBottom = debounce(() => {
    const scrollTop = window.scrollY
    const documentHeight = document.documentElement.scrollHeight
    const windowHeight = window.innerHeight

    if (scrollTimeout) clearTimeout(scrollTimeout)

    if (scrollTop + windowHeight >= documentHeight) {
      const activeElement = document.querySelector(".filter-content-list > a.active") as HTMLElement
      const currentView = searchTerm ? "search" : activeElement?.dataset?.target || ""

      scrollTimeout = window.setTimeout(() => {
        window.App.Utils?.logCustomDatadogAction("get_more_scroll_to_bottom", {
          currentView,
          searchTerm,
        })
      }, 1000)
    }
  }, 150)

  window.addEventListener("scroll", debouncedHandleScrollToBottom)
}
